import Modal from "@/Components/Modal";
import React, {useMemo} from "react";
import CloseIcon from "@/Components/Icons/CloseIcon";
import {PersistedExtractionJob} from "@/types";
import moment from "moment";
import {formatMinutes, ucFirst, secondsToDuration, getLanguageName} from "@/utils/textFormatting";
import {Moment} from "moment/moment";
import toast from "react-hot-toast";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClipboard} from "@fortawesome/free-regular-svg-icons";

export type ExtractionJobDetailsModalProps = {
    job: PersistedExtractionJob,
    metadata: Record<string, any>,
    lastModifiedAt: Moment | null,
    show: boolean,
    onClose: () => void,
}

export default function ({job, metadata, lastModifiedAt, onClose, show}: ExtractionJobDetailsModalProps) {
    const details = useMemo(() => {
        return {
            'ID': job.job_name,
            'Status': job.status,
            'Method': ucFirst(job.method === 'ocr' ? 'vision' : job.method),
            'Extraction Language': getLanguageName(job.language),
            'File Duration': secondsToDuration(job.file_duration),
            'Start Time': metadata.timeStart ? secondsToDuration(metadata.timeStart) : null,
            'Stop Time': metadata.timeEnd ? secondsToDuration(metadata.timeEnd) : null,
            'Processing duration': job.runtime_duration ? formatMinutes(job.runtime_duration / 60, true) : null,
            'Created': moment(job.created_at).format('YYYY-MM-DD HH:mm:ss'),
            'Last Modified': lastModifiedAt ? lastModifiedAt.format('YYYY-MM-DD HH:mm:ss') : null,
            'Subtitle Position': metadata.subtitlePosition ? ucFirst(metadata.subtitlePosition) : null,
            'Source': metadata.source ? ucFirst(metadata.source) : null,
        }
    }, [job, metadata, lastModifiedAt]);

    return (
        <Modal maxWidth={'xl'} show={show} onClose={onClose}>
            <div id="default-modal" className="w-full">
                <div className="flex items-center p-4 md:p-7 !pb-4">
                    <div className="flex-grow flex">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                            Details
                        </h3>
                    </div>
                    <button type="button"
                            onClick={onClose}
                            className="text-gray-400 dark:text-gray-500 bg-transparent hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                    >
                        <CloseIcon/>
                        <span className="sr-only">Close modal</span>
                    </button>
                </div>
                <div className="px-6 pb-7">
                    <div className=" overflow-hidden">
                        <table className="w-full">
                            <tbody>
                            {Object.entries(details)
                                .filter(([_, val]) => val !== null)
                                .map(([key, value], index) => (
                                    <tr key={key} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-900'}>
                                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            {key}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                            <span className="flex items-center gap-2">
                                                {ucFirst(value!)}
                                                {key === 'ID' && (
                                                    <button
                                                        onClick={() => navigator.clipboard.writeText(value!).then(() => toast.success('Copied to clipboard'))}
                                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                    >
                                                        <FontAwesomeIcon icon={faClipboard} className="w-3.5 h-3.5"/>
                                                    </button>
                                                )}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
